import React, { useEffect, useState } from 'react';
import { RootStateOrAny, useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import {
  Checkbox,
  FormControl,
  Grid,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  Select,
  Tooltip,
} from '@material-ui/core';
import { setSpecificSpecialEventData } from '../../../redux/actions/newOperationHoursActions';
import { formatTime } from '../../../utils/utils';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    formControl: {
      margin: theme.spacing(1),
      minWidth: 120,
    },
    selectEmpty: {
      marginTop: theme.spacing(2),
    },
    root: {
      flexGrow: 1,
      borderBottom: '1px solid',
      borderBottomColor: '#bdbdbd',
      marginTop: 5,
    },
    demo: {
      backgroundColor: '#f5f5f5',
    },
  }),
);

// eslint-disable-next-line import/no-anonymous-default-export
export default function RowCurbside({ disableAll, restaurantId = '' }) {
  const { t } = useTranslation();
  const classes = useStyles();
  const dispatch: any = useDispatch();

  const {
    curbsideTimeOpenMode,
    curbsideTimeCloseMode,
    curbsideTimeOpen,
    curbsideTimeClose,
    curbsideIsClosed,
    curbsideIsClosedMode,
    availableTimes,
    is12HFormat,
  } = useSelector((state: RootStateOrAny) => state.NewOperationHoursReducer);
  const restaurantServices = useSelector(
    (state: RootStateOrAny) => state.ServicesReducer.entities,
  );
  const curbsideService = restaurantServices.find(
    elem => elem.code === 'curbsidePickup',
  );

  const [selectOpenSelectedIndex, setSelectOpenSelectedIndex] = useState(0);

  const disabledSelects = disableAll || curbsideIsClosed === 'Y';

  const onChangeSelectOpen = event => {
    const select = event.target as HTMLSelectElement;
    if (curbsideTimeOpen !== select.value) {
      dispatch(
        setSpecificSpecialEventData({
          curbsideTimeOpen: select.value,
        }),
      );
    }
  };

  const onChangeSelectClose = event => {
    const select = event.target as HTMLSelectElement;
    if (curbsideTimeClose !== select.value) {
      dispatch(
        setSpecificSpecialEventData({
          curbsideTimeClose: select.value,
        }),
      );
    }
  };

  useEffect(() => {
    const index = availableTimes.findIndex(elem => elem === curbsideTimeOpen);
    setSelectOpenSelectedIndex(index);
  }, [curbsideTimeOpen]);

  const onChangeCheckboxIsClosed = e => {
    const checked = e.target.checked;

    const newState: any = {};

    if (checked) {
      newState.curbsideIsClosed = 'Y';

      if (curbsideIsClosed === 'Y' && hasCloseCurbside()) {
        newState.closeToAll = true;
      }
    } else {
      newState.curbsideIsClosed = 'N';
      newState.closeToAll = false;
    }

    dispatch(setSpecificSpecialEventData(newState));
  };

  const getValueSelectOpen = () => {
    if (curbsideIsClosed === 'Y') {
      return '';
    } else {
      return curbsideTimeOpen ? curbsideTimeOpen : '';
    }
  };

  const getValueSelectClose = () => {
    if (curbsideIsClosed === 'Y') {
      return '';
    } else {
      return curbsideTimeClose ? curbsideTimeClose : '';
    }
  };

  return (
    <div className={classes.root}>
      <Grid container spacing={0}>
        <Grid item xs={4} md={4}>
          <div className={classes.demo}>
            <List>
              <ListItem>
                <ListItemText
                  primary={t('operationHours.curbsideHour')}
                  primaryTypographyProps={{ display: 'inline' }}
                  secondary={!restaurantId ? <sup>++</sup> : ''}
                  secondaryTypographyProps={
                    !restaurantId
                      ? {
                          display: 'inline',
                          style: { fontSize: '12px', fontStyle: 'italic' },
                        }
                      : undefined
                  }
                />
              </ListItem>
            </List>
          </div>
        </Grid>
        <Grid item xs={1} md={1}>
          <Checkbox style={{ marginTop: 10, visibility: 'hidden' }} />
        </Grid>
        <Grid item xs={1} md={1}>
          <Checkbox
            style={{ marginTop: 10 }}
            disabled={disableAll || !curbsideIsClosedMode.includes('write')}
            checked={curbsideIsClosed === 'Y'}
            onChange={onChangeCheckboxIsClosed}
          />
        </Grid>
        <Grid item xs={3} md={3}>
          {curbsideTimeOpenMode.includes('read') ? (
            <Tooltip title={''}>
              <FormControl variant="outlined" className={classes.formControl}>
                <InputLabel htmlFor="outlined-time-native-simple">
                  {t('operationHours.start')}
                </InputLabel>
                <Select
                  style={
                    disabledSelects || !curbsideTimeOpenMode.includes('write')
                      ? { pointerEvents: 'none', color: 'grey' }
                      : undefined
                  }
                  native
                  value={getValueSelectOpen()}
                  label={t('operationHours.start')}
                  inputProps={{
                    name: 'availableTimes',
                    id: 'outlined-time-native-simple',
                  }}
                  onChange={onChangeSelectOpen}
                  disabled={
                    disabledSelects || !curbsideTimeOpenMode.includes('write')
                  }
                >
                  {availableTimes?.map((elem, index) => (
                    <option key={index} value={elem}>
                      {formatTime(elem, is12HFormat)}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </Tooltip>
          ) : null}
        </Grid>
        <Grid item xs={3} md={3}>
          {curbsideTimeCloseMode.includes('read') ? (
            <Tooltip title={''}>
              <FormControl variant="outlined" className={classes.formControl}>
                <InputLabel htmlFor="outlined-time-native-simple">
                  {t('operationHours.close')}
                </InputLabel>
                <Select
                  style={
                    disabledSelects || !curbsideTimeCloseMode.includes('write')
                      ? { pointerEvents: 'none', color: 'grey' }
                      : undefined
                  }
                  native
                  value={getValueSelectClose()}
                  label={t('operationHours.close')}
                  inputProps={{
                    name: 'availableTimes',
                    id: 'outlined-time-native-simple',
                  }}
                  onChange={onChangeSelectClose}
                  disabled={
                    disabledSelects || !curbsideTimeCloseMode.includes('write')
                  }
                >
                  {availableTimes
                    ?.filter(
                      (elem, index) =>
                        index > selectOpenSelectedIndex || index === 0,
                    )
                    .map((elem, index) => (
                      <option key={index} value={elem}>
                        {formatTime(elem, is12HFormat)}
                      </option>
                    ))}
                </Select>
              </FormControl>
            </Tooltip>
          ) : null}
        </Grid>
      </Grid>
    </div>
  );
  function isCurbsideClosed() {
    return curbsideIsClosed === 'Y';
  }

  function hasCurbside() {
    return curbsideService?.value;
  }

  function hasCloseCurbside() {
    return !hasCurbside() || isCurbsideClosed();
  }
}

RowCurbside.displayName = 'RowCurbside';
